import * as CL from '@design-system/component-library';
import { AddonConfirmationDialog } from './AddonConfirmationDialog';
import { DialogType, SubscriptionAddonCode, SubscriptionCategory } from '../../common/enums';
import { EcommerceEventTypeV4 } from '../../common/analytics';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import {
  calculateServiceFee,
  calculateUpdatedPrice,
  checkCurrentCombination,
  getAddOnPrice,
  getAddOnsByCodes,
  reachabilityDefaultValues,
} from '../SubscriptionDetails/addOnDependencyUtils';
import { changeSubscriptionFeatures } from '../../common/fetch';
import { dsClass } from '../../common/constants/dsClasses';
import { generatePath, useNavigate } from 'react-router-dom';
import { getReachabilityWithMobilePayload, pushAnalyticsEvent } from '../PbxAccordionContent/PbxAccordionUtils';
import { isReachabilityWithMobile } from '../../common/utils/subscriptionUtils';
import { paths } from '../../common/constants/pathVariables';
import { showDialog, startNotification } from '../../selfservice/actions';
import { somethingWentWrongMsg, t } from '../../common/i18n';
import { useDispatch } from 'react-redux';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { useState } from 'react';
import type { AddOnVisibility } from '../../generated/api/addOnVisibility';
import type { AssociationRecord, DependencyRecord } from '@onlinefirst/cloudsense-add-on-dependency-engine';
import type { BaseRules } from '../SubscriptionDetails/addOnDependencyUtils';
import type { DialogParams } from '../../common/types/dialog';
import type { Subscription } from '../../generated/api/subscription';
import type { SubscriptionStatus } from '../../common/types/subscription';

interface AddReachabilityWithMobileAddonProps {
  subscription: Subscription;
  addOnRulesAssociations?: AssociationRecord[];
  addOnRulesDependencies?: DependencyRecord[];
  addOnVisibilities?: AddOnVisibility[];
  subscriptionStatus: SubscriptionStatus;
  analyticsBundleId: string;
}

const checkIsAddonCombinationValid = (
  subscription: Subscription,
  addOnRulesAssociations: AssociationRecord[] | undefined,
  addOnRulesDependencies: DependencyRecord[] | undefined,
  addOnVisibilities: AddOnVisibility[] | undefined
) => {
  const subscriptionAddOnCodes = subscription.details?.selectedAddOns?.map(addOn => addOn.addOnCode) || [];
  const baseRules: BaseRules = {
    commercialProductId: subscription.commercialProductId || '',
    commercialProductCode: subscription.commercialProductCode || '',
    addOnAssociations: addOnRulesAssociations || [],
    addOnDependencies: addOnRulesDependencies || [],
  };
  const combination = checkCurrentCombination(
    baseRules,
    [SubscriptionAddonCode.REACHABILITY_WITH_MOBILE],
    [],
    subscriptionAddOnCodes,
    addOnVisibilities || []
  );
  return (
    combination.isValid && addOnVisibilities?.some(visibility => isReachabilityWithMobile(visibility.addOn?.addOnCode))
  );
};

export const AddReachabilityWithMobileAddon = ({
  subscription,
  addOnRulesAssociations,
  addOnRulesDependencies,
  addOnVisibilities,
  subscriptionStatus,
  analyticsBundleId,
}: AddReachabilityWithMobileAddonProps) => {
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const dispatch = useDispatch();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const onShowDialog = (params: DialogParams) => dispatch(showDialog(params));

  const addOnVisibility = addOnVisibilities?.find(visibility => isReachabilityWithMobile(visibility.addOn?.addOnCode));
  const addedAddOns = getAddOnsByCodes(
    [SubscriptionAddonCode.REACHABILITY_WITH_MOBILE],
    addOnVisibilities || [],
    subscription.details?.selectedAddOns
  );
  const priceInfo = { value: getAddOnPrice(addOnVisibility?.addOn?.price?.effectivePrice).text || '-' };
  const isCurrentCombinationValid = checkIsAddonCombinationValid(
    subscription,
    addOnRulesAssociations,
    addOnRulesDependencies,
    addOnVisibilities
  );

  const onConfirmAddReachabilityAddon = async () => {
    if (subscription?.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      onShowDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
      return;
    }
    if (subscriptionStatus.pendingActions) {
      onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
      return;
    }
    const payload = getReachabilityWithMobilePayload(addedAddOns, [], [], subscription, {
      [SubscriptionAddonCode.REACHABILITY_WITH_MOBILE]: reachabilityDefaultValues,
    });

    try {
      setIsSaving(true);
      await changeSubscriptionFeatures(payload, subscription.subscriptionId, mdmId);
      setIsSaving(false);
      pushAnalyticsEvent(addedAddOns, EcommerceEventTypeV4.PURCHASE, analyticsBundleId);
      dispatch(startNotification(t.T29V('Change order successful'), 'success'));
      navigate(
        `${generatePath(paths.PS_MOBILE_SUBSCRIPTION, {
          subscriptionId: subscription?.subscriptionDisplayId,
        })}${location.search}`
      );
    } catch (error) {
      dispatch(startNotification(t.JDTO(somethingWentWrongMsg), 'error'));
      setIsSaving(false);
    }
    setShowConfirmDialog(false);
  };

  return (
    <>
      <div className="of-pbx-additional_services__addon">
        <div className="of-pbx-additional_services__addon__name">
          <div className={`${dsClass.PADDING_BOTTOM_2} ${dsClass.PADDING_TOP_3}`}>
            {t.VSWS('Reachability with mobile number')}
          </div>
          <div className={`${dsClass.PADDING_BOTTOM_2} ${dsClass.PADDING_TOP_3} ${dsClass.PADDING_RIGHT_3}`}>
            {priceInfo?.value}
          </div>
        </div>

        <div className={`${dsClass.FONT_SIZE_SMALLER}`}>
          {t.WXYJ(
            'You can add backup respondents for the mobile phone number. Vakio’s call features are now available for calls to mobile phone numbers as well. '
          )}
          <CL.Link
            className={dsClass.FONT_SIZE_MEDIUM}
            linkTarget="_blank"
            linkHref="https://yrityksille.elisa.fi/ohjeet/vakio-vinkit"
          >
            {t.HKO6('Read more about reachability with mobile phone number')}
            <CL.Icon icon="external-link" />
          </CL.Link>
        </div>
        <div className={`${dsClass.PADDING_TOP_3} ${dsClass.PADDING_BOTTOM_3}`}>
          <CL.Button
            disabled={!isCurrentCombinationValid}
            onClick={() => {
              setShowConfirmDialog(true);
            }}
          >
            {t.XFA9('Activate for')} {priceInfo?.value}
          </CL.Button>
        </div>
      </div>
      {showConfirmDialog && (
        <AddonConfirmationDialog
          addedAddOns={addedAddOns}
          isEmployee={false}
          removedAddOns={[]}
          onCloseDialog={() => setShowConfirmDialog(false)}
          onConfirm={onConfirmAddReachabilityAddon}
          serviceFee={calculateServiceFee(addedAddOns)}
          updatedPrice={calculateUpdatedPrice(addedAddOns, [], SubscriptionCategory.VOICE, subscription)}
          isSaving={isSaving}
        />
      )}
    </>
  );
};
